import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PuckColorField from '../../../puck/fields/PuckColorField.jsx';
import { usePuckRichTextStyle } from '../../../context/PuckRichTextStyleContext';
import { normalizeCssColor } from '../../../utils/proposalRichTextPalette';
import { getSelectionHighlightColor } from '../../../utils/richTextSelectionColor';

/**
 * Surlignage du texte dans le bubble menu — même composant de couleur que la
 * couleur du texte, avec `allowTransparent` : la pastille transparente retire
 * le surlignage. Remplace les champs « couleur de fond du texte » de la
 * sidebar, qui coloraient le bloc entier et pas une sélection.
 */
export default function RichTextBubbleHighlightControls({
  editor,
  rememberNow,
  runWithSelection,
  onRestoreSelection,
  lockBubbleMenu,
  unlockBubbleMenu,
}) {
  const { swatches } = usePuckRichTextStyle();
  const [selectionColor, setSelectionColor] = useState(null);

  const swatchColors = useMemo(
    () => swatches.map((s) => s.value).filter(Boolean),
    [swatches],
  );

  useEffect(() => {
    if (!editor) return undefined;

    const syncFromSelection = () => {
      setSelectionColor(getSelectionHighlightColor(editor));
    };

    syncFromSelection();
    editor.on('selectionUpdate', syncFromSelection);
    editor.on('transaction', syncFromSelection);
    return () => {
      editor.off('selectionUpdate', syncFromSelection);
      editor.off('transaction', syncFromSelection);
    };
  }, [editor]);

  const applyHighlight = useCallback((color, { refocusEditor = true } = {}) => {
    rememberNow();
    // Vide = pastille transparente : on retire la mark au lieu de surligner en
    // transparent, sinon le HTML garde un <mark> invisible.
    if (!color || color === 'transparent') {
      runWithSelection((chain) => chain.unsetHighlight(), { focus: refocusEditor });
      return;
    }
    const normalized = normalizeCssColor(color);
    if (!normalized) return;
    runWithSelection(
      (chain) => chain.setHighlight({ color: normalized }),
      { focus: refocusEditor },
    );
  }, [rememberNow, runWithSelection]);

  return (
    <PuckColorField
      variant="bubble"
      glyph="highlight"
      value={selectionColor ?? ''}
      onChange={applyHighlight}
      swatches={swatchColors}
      size="xs"
      allowTransparent
      rememberNow={rememberNow}
      lockBubbleMenu={lockBubbleMenu}
      unlockBubbleMenu={unlockBubbleMenu}
      onRestoreSelection={onRestoreSelection}
    />
  );
}
